/**
 * CancelOrderDrawer — slide-out panel for admin-initiated order cancellation.
 *
 * Mirrors ReprintDrawer: outer component owns the mutation + toast + query
 * invalidation + close guard; inner component owns form state so it resets
 * when the drawer unmounts on close.
 *
 * The trimmed reason is sent with the V2 mutation and persisted on the
 * resulting OrderEvent; MAX_REASON_LENGTH mirrors the backend @Size cap so
 * the input caps at the same length the server will accept.
 */
import { Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { useToasts } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { Body, Label } from "../../components/Typography";
import { RpaTextInput } from "../../components/inputFields/RpaTextInput";

export const CANCEL_ORDER_TOAST = {
  success: "Order cancellation request submitted successfully!",
  failure: "Failed to process cancellation request",
} as const;

const MAX_REASON_LENGTH = 500;

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing[3],
    padding: tokens.spacing[6],
  },
  cancelLink: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    alignSelf: "center",
  },
  cancelText: {
    color: tokens.color.semantic.text.link,
    borderBottom: "1px solid currentColor",
    paddingBottom: 2,
  },
});

interface CancelOrderDrawerProps {
  opened: boolean;
  onClose: () => void;
  order: GraphQL.OrderMinimalQuery["orderByIdentifier"];
}

export function CancelOrderDrawer({ opened, onClose, order }: CancelOrderDrawerProps) {
  const { showSuccessToast, showErrorToast } = useToasts();
  const queryClient = useQueryClient();

  const { mutate: submitCancel, isLoading } = GraphQL.useAdminCancelOrderV2Mutation({
    onSuccess: async () => {
      showSuccessToast(CANCEL_ORDER_TOAST.success);
      await queryClient.invalidateQueries({ queryKey: ["Order"] });
      onClose();
    },
    onError: (error) => {
      showErrorToast(isGraphQLError(error) ? error.message : CANCEL_ORDER_TOAST.failure);
    },
  });

  // Block close during in-flight mutation so the user doesn't orphan a cancel request.
  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const handleSubmit = (reason: string) => {
    if (!order?.customer?.publicId || !order?.customerOrderId) return;
    submitCancel({
      input: {
        orderIdentifier: {
          customerPublicId: order.customer.publicId,
          customerOrderId: order.customerOrderId,
        },
        reason,
      },
    });
  };

  return (
    <RpaDrawer
      title={<span data-testid="cancel-order-drawer-title">{`Cancel Order: ${order?.customerOrderId ?? ""}`}</span>}
      opened={opened}
      onClose={handleClose}
    >
      <CancelOrderDrawerContents isLoading={isLoading} onClose={handleClose} onSubmit={handleSubmit} />
    </RpaDrawer>
  );
}

interface CancelOrderDrawerContentsProps {
  isLoading: boolean;
  onSubmit: (reason: string) => void;
  onClose: () => void;
}

function CancelOrderDrawerContents({ isLoading, onSubmit, onClose }: CancelOrderDrawerContentsProps) {
  const { classes } = useStyles();
  const [reason, setReason] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  const trimmedReason = reason.trim();
  const isReasonBlank = trimmedReason.length === 0;

  const handleSubmit = () => {
    setSubmitted(true);
    if (isReasonBlank) return;
    onSubmit(trimmedReason);
  };

  return (
    <>
      <div className={classes.body}>
        <RpaTextInput
          data-testid="cancel-order-reason-input"
          label={
            <Label size="base" weight="semibold">
              Reason
            </Label>
          }
          placeholder="Enter"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          maxLength={MAX_REASON_LENGTH}
          error={submitted && isReasonBlank ? "Please provide a reason" : undefined}
        />
      </div>
      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button
            data-testid="cancel-order-submit"
            variant="filled"
            intent="danger"
            size="large"
            loading={isLoading}
            onClick={handleSubmit}
          >
            Cancel Order
          </Button>
          <button type="button" className={classes.cancelLink} onClick={onClose} disabled={isLoading}>
            <Body size="base" weight="semibold" className={classes.cancelText}>
              Never Mind
            </Body>
          </button>
        </div>
      </RpaDrawer.Footer>
    </>
  );
}
